/**
 * Rounding method grabbed from this URL: https://codepen.io/jordanwillis/pen/jBoppb
 */

export default function applyRoundedRectangleFeature(chart): void {
  // draws a rectangle with a rounded top
  chart.helpers.drawRoundedTopRectangle = function (
    ctx,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    if (height) {
      ctx.beginPath()
      ctx.moveTo(x + radius, y)
      // top right corner
      ctx.lineTo(x + width - radius, y)
      ctx.arc(
        x + width - radius,
        y + radius,
        radius,
        1.5 * Math.PI,
        0 * Math.PI
      )
      // bottom right corner
      ctx.lineTo(x + width, y + height)
      // bottom left corner
      ctx.lineTo(x, y + height)
      // top left
      ctx.lineTo(x, y + radius)
      ctx.arc(x + radius, y + radius, radius, 1 * Math.PI, 1.5 * Math.PI)
      ctx.closePath()
    } else {
      const minHeightOfColumn = ctx.canvas.height / 100
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(x, y - minHeightOfColumn)
      ctx.lineTo(x + width, y - minHeightOfColumn)
      ctx.lineTo(x + width, y)
      ctx.closePath()
    }
  }

  chart.elements.RoundedTopRectangle = chart.elements.Rectangle.extend({
    draw() {
      const ctx = this._chart.ctx
      const vm = this._view
      let left: number
      let right: number
      let top: number
      let bottom: number
      let signX: number
      let signY: number
      let borderSkipped: string
      let borderWidth: number = vm.borderWidth

      if (!vm.horizontal) {
        // bar
        left = vm.x - vm.width / 2
        right = vm.x + vm.width / 2
        top = vm.y
        bottom = vm.base
        signX = 1
        signY = bottom > top ? 1 : -1
        borderSkipped = vm.borderSkipped || 'bottom'
      } else {
        // horizontal bar
        left = vm.base
        right = vm.x
        top = vm.y - vm.height / 2
        bottom = vm.y + vm.height / 2
        signX = right > left ? 1 : -1
        signY = 1
        borderSkipped = vm.borderSkipped || 'left'
      }

      // Canvas doesn't allow us to stroke inside the width so we can
      // adjust the sizes to fit if we're setting a stroke on the line
      if (borderWidth) {
        // borderWidth should be less than bar width and bar height.
        const barSize: number = Math.min(
          Math.abs(left - right),
          Math.abs(top - bottom)
        )
        borderWidth = borderWidth > barSize ? barSize : borderWidth
        const halfStroke: number = borderWidth / 2
        // Adjust borderWidth when bar top position is near vm.base(zero).
        const borderLeft: number =
          left + (borderSkipped !== 'left' ? halfStroke * signX : 0)
        const borderRight: number =
          right + (borderSkipped !== 'right' ? -halfStroke * signX : 0)
        const borderTop: number =
          top + (borderSkipped !== 'top' ? halfStroke * signY : 0)
        const borderBottom: number =
          bottom + (borderSkipped !== 'bottom' ? -halfStroke * signY : 0)
        // not become a vertical line?
        if (borderLeft !== borderRight) {
          top = borderTop
          bottom = borderBottom
        }
        // not become a horizontal line?
        if (borderTop !== borderBottom) {
          left = borderLeft
          right = borderRight
        }
      }

      // calculate the bar width and roundess
      const barWidth: number =
        this._chart.config.options.barWidth || Math.abs(left - right)
      const roundness: number = this._chart.config.options.barRoundness || 0.5
      const radius: number = barWidth * roundness * 0.5

      // keep track of the original top of the bar
      const prevTop: number = top

      // move the top down so there is room to draw the rounded top
      top = prevTop + radius
      const barRadius: number = top - prevTop

      ctx.beginPath()
      ctx.fillStyle = vm.backgroundColor
      ctx.strokeStyle = vm.borderColor
      ctx.lineWidth = borderWidth

      // draw the rounded top rectangle
      chart.helpers.drawRoundedTopRectangle(
        ctx,
        left,
        top - barRadius + 1,
        barWidth,
        bottom - prevTop,
        barRadius
      )

      ctx.fill()
      if (borderWidth) {
        ctx.stroke()
      }

      // restore the original top value so tooltips and scales still work
      top = prevTop
    }
  })

  chart.defaults.roundedBar = chart.helpers.clone(chart.defaults.bar)

  chart.defaults.global.datasets.roundedBar = chart.defaults.global.datasets.bar

  chart.controllers.roundedBar = chart.controllers.bar.extend({
    dataElementType: chart.elements.RoundedTopRectangle
  })
}
